/**
 * Situação da assinatura mostrada ao operador: fonte única para a listagem de assinaturas, o
 * detalhe dela e o detalhe da assinatura na venda.
 *
 * Antes a venda mostrava o "último status" gravado pela integração (integrationLastStatus),
 * enquanto a listagem usava esta regra; o mesmo cliente aparecia "Ativo" em Assinaturas e
 * "Aguardando pagamento" na venda.
 *
 * A primeira situação que se aplica vence (docs/subscription/subscriber/README.md, "Situação
 * exibida na listagem"). Os campos vêm do resumo de cobranças do backend
 * (SubscriptionService.enrichChargeSummary).
 *
 * @param {object=} subscription assinatura com cancellationDate, storePackageEnded,
 *        paymentLinkExpired, pendingFirstPayment, hasOverdueCharge, providerSubscriptionStatus e status
 * @returns {{key: string, tone: string}} key é a chave de tradução do rótulo
 *          (cancelled, package_ended, payment_link_expired, awaiting_payment, overdue,
 *          up_to_date); tone é a
 *          tonalidade do selo (inactive, pending, overdue, active)
 */
export const getSubscriptionSituation = (subscription) => {
    if (subscription?.cancellationDate) {
        // Pacote da loja que venceu sem renovação não foi cancelado por ninguém: ele acabou.
        // Separar os dois diz a quem atende que o caminho é oferecer a renovação.
        return subscription?.storePackageEnded
            ? { key: "package_ended", tone: "inactive" }
            : { key: "cancelled", tone: "inactive" };
    }

    // Link vencido vem antes de "aguardando pagamento" porque é mais específico: não há mais o
    // que esperar daquele link, e quem lê precisa saber que o caminho é gerar outro. O contrato
    // em si continua aberto.
    if (subscription?.paymentLinkExpired) {
        return { key: "payment_link_expired", tone: "pending" };
    }

    // Nunca pagou vem antes de "em atraso" e de "ativa": contrato que nunca recebeu pagamento
    // não é assinante devendo, é assinante que ainda não começou. O contrato pode estar ACTIVE
    // no provedor sem ter entrado dinheiro nenhum, inclusive quando o único pagamento do ciclo
    // foi estornado. Mesma precedência da coroa apagada na listagem de clientes.
    if (subscription?.pendingFirstPayment) {
        return { key: "awaiting_payment", tone: "pending" };
    }

    if (subscription?.hasOverdueCharge) {
        return { key: "overdue", tone: "overdue" };
    }

    const providerStatus = subscription?.providerSubscriptionStatus;
    if ((providerStatus && providerStatus.toLowerCase() !== "active")
        || (!providerStatus && subscription?.status === "INACTIVE")) {
        return { key: "awaiting_payment", tone: "pending" };
    }

    // Chave própria, e não a genérica "active": essa é o "Ativo" de todo formulário, e o que
    // esta situação diz é que o cliente pagou e não tem mensalidade vencida, ou seja, está em
    // dia. "Ativo" ao lado de "Em atraso" fazia parecer que o atrasado estava inativo.
    return { key: "up_to_date", tone: "active" };
};
